import type { AccesoEnElMenu, Contador, EstadoSuscripcion, ModoSuscripcion } from '../api/operaciones';
import { motivoSoloLectura } from './soloLectura';

/**
 * Franjas del estado de la cuenta (0.5, RV Franja; P0-9). Es la única excepción a «los avisos, en la campana»: habla del
 * estado de la cuenta, no de un hecho puntual. **Se ve una sola, la más grave**; las demás van a la campana. Las
 * amarillas (prueba, baja, cupo) se pueden cerrar y siguen en la campana; lo cerrado se recuerda en el servidor
 * (`dismissed_notices` de getMyNavigation, G129) con una clave por franja que lleva la fecha o el contador del que habla,
 * para que una franja nueva del mismo tipo vuelva a salir. Orden y textos: los del diseño (0.1).
 *
 * El acceso de plataforma abierto llega en `platform_access` de getMyNavigation (G137): la persona de Reservaria ve el
 * suyo en índigo (`--rv-soporte`): «suplantación» y «reservaria» (Soporte en solo lectura), también tras recargar; el
 * propietario y quien tiene `actividad.ver` ven la franja negra «Soporte de Reservaria está consultando tu negocio».
 *
 * Sin nada visual: el marco pinta cada franja con RV Franja.
 */
export type TipoFranja = 'suplantacion' | 'reservaria' | 'solo-lectura' | 'impago' | 'soporte' | 'cupo100' | 'prueba' | 'baja' | 'cupo';

/** De más a menos grave (P0-9, 0.5; G777: el cupo al 100 %, detrás de soporte y delante de prueba). */
export const ORDEN_FRANJAS: readonly TipoFranja[] = ['suplantacion', 'reservaria', 'solo-lectura', 'impago', 'soporte', 'cupo100', 'prueba', 'baja', 'cupo'];

/** Solo las amarillas se cierran; las rojas (también el cupo al 100 %, G777) y las de Soporte, nunca (§15). */
export const FRANJAS_CERRABLES: readonly TipoFranja[] = ['prueba', 'baja', 'cupo'];

/**
 * Adónde lleva la acción: a la entrada `plan`, a su apartado de consumo (`?apartado=consumo`, el cupo al 100 %), a la de
 * `actividad` o a cerrar el acceso de plataforma.
 */
export type DestinoFranja = 'plan' | 'consumo' | 'actividad' | 'salir';

export interface Franja {
    tipo: TipoFranja;
    /**
     * Clave con la que se recuerda cerrada (`DismissedNotice.key`): su tipo y la fecha o el contador del que habla
     * (`trial_ending:2026-10-23`, `usage_90:conversations_2026-10-01`); null en las que no se cierran.
     */
    clave: string | null;
    titulo: string;
    /** El del móvil (`tituloCorto`). */
    tituloCorto: string;
    /** El texto largo: va en el bocadillo y para el lector de pantalla (franja compacta). */
    texto: string;
    accion: string | null;
    destino: DestinoFranja | null;
    cerrable: boolean;
}

export interface DatosFranjas {
    /** Nombre del negocio. */
    negocio: string;
    /** Nombre de quien está en la sesión (para «Carlos Vega, de Reservaria» en la suplantación). */
    persona: string;
    estado: EstadoSuscripcion | null;
    modo: ModoSuscripcion;
    propietario: boolean;
    /** Tiene `plan.ver`: ve el cupo y las fechas de `getSubscription` (P0-9). */
    puedeVerPlan: boolean;
    /** Fin de la prueba (`trial_ends_at`), fin de la gracia del impago (`grace_ends_at`) y fecha de la baja (`cancel_at`). */
    finPrueba: string | null;
    finGracia: string | null;
    fechaBaja: string | null;
    contadores: readonly Contador[];
    /** Acceso de plataforma abierto (`platform_access` de getMyNavigation, G137), o null. */
    acceso: AccesoEnElMenu | null;
    ahora: Date;
}

const DIA_MS = 86_400_000;

/** «1 de noviembre», como el diseño. */
export function diaYMes(fecha: string): string {
    return new Date(fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
}

/** Días que quedan hasta una fecha (redondeando hacia arriba; 0 si ya pasó). */
export function diasHasta(fecha: string, ahora: Date): number {
    return Math.max(0, Math.ceil((new Date(fecha).getTime() - ahora.getTime()) / DIA_MS));
}

/** Minutos que quedan hasta que caduca el acceso de plataforma. */
export function minutosHasta(fecha: string, ahora: Date): number {
    return Math.max(0, Math.ceil((new Date(fecha).getTime() - ahora.getTime()) / 60_000));
}

/** «14:05»: la hora a la que se abrió el acceso de Soporte. */
export function horaDe(fecha: string): string {
    return new Date(fecha).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

/**
 * El contador de conversaciones del asistente con lo usado ≥ `parte` de lo incluido más lo comprado. Al 100 % sale la
 * franja roja (G777) y no la del 90 %, que diría lo mismo con menos.
 */
function cupoAl(contadores: readonly Contador[], desde: number, hasta = Infinity): Contador | undefined {
    return contadores.find((c) => {
        const total = c.included + c.extra;
        return c.concept === 'conversations' && total > 0 && c.used >= total * desde && c.used < total * hasta;
    });
}

/** «1 de noviembre»: el día siguiente al último del periodo (`period_end` es el último día incluido, G335). */
function diaSiguiente(ultimo: string): string {
    const [a, m, d] = ultimo.slice(0, 10).split('-').map(Number);
    const siguiente = new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, (d ?? 1) + 1));
    return siguiente.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', timeZone: 'UTC' });
}

/** La fecha (AAAA-MM-DD) para la clave de una franja cerrada (`DismissedNotice.key` no admite «:» en la referencia). */
const diaIso = (fecha: string) => fecha.slice(0, 10);

function franja(tipo: TipoFranja, datos: Omit<Franja, 'tipo' | 'cerrable' | 'clave'>, clave: string | null = null): Franja {
    const cerrable = FRANJAS_CERRABLES.includes(tipo);
    return { tipo, ...datos, cerrable, clave: cerrable ? clave : null };
}

/** Todas las franjas que tocan ahora, de más a menos grave. */
export function franjasDe(d: DatosFranjas): Franja[] {
    const lista: Franja[] = [];
    const prop = d.propietario;
    const acceso = d.acceso;

    if (acceso !== null && acceso.viewer === 'platform') {
        const min = minutosHasta(acceso.expires_at, d.ahora);
        if (acceso.mode === 'impersonation') {
            const quien = acceso.impersonated_member_name ?? 'el propietario';
            lista.push(franja('suplantacion', {
                titulo: `Actuando como ${quien} en ${d.negocio} · caduca en ${String(min)} min`,
                tituloCorto: `Actuando como… · ${String(min)} min`,
                texto: `${d.persona}, de Reservaria · caduca en ${String(min)} min. No puedes reembolsar ni tocar la cuenta de cobro.`,
                accion: 'Salir',
                destino: 'salir',
            }));
        } else {
            lista.push(franja('reservaria', {
                titulo: `Soporte en ${d.negocio}, solo lectura · caduca en ${String(min)} min · Motivo: ${acceso.reason ?? ''}`,
                tituloCorto: `Soporte · ${d.negocio} · ${String(min)} min`,
                texto: `Motivo: «${acceso.reason ?? ''}» · caduca en ${String(min)} min.`,
                accion: 'Salir',
                destino: 'salir',
            }));
        }
    }

    const ro = motivoSoloLectura(d.modo, d.estado, acceso !== null && acceso.viewer === 'platform' && acceso.mode === 'read');
    if (ro !== null) {
        lista.push(franja('solo-lectura', {
            titulo: ro.motivo === '' ? 'Solo lectura' : `Solo lectura por ${ro.motivo}`,
            tituloCorto: ro.motivo === '' ? 'Solo lectura' : `Solo lectura: ${ro.motivo}`,
            texto: d.estado === 'suspended'
                ? prop
                    ? 'Paga la cuota pendiente y todo vuelve a funcionar. Tus clientes pueden cancelar.'
                    : 'Hasta que el propietario pague la cuota, puedes consultar pero no cambiar nada.'
                : 'Puedes consultar, pero no crear ni cambiar nada. Tus clientes pueden cancelar.',
            accion: prop ? ro.accion : null,
            destino: prop && ro.accion !== null ? 'plan' : null,
        }));
    }

    if (d.estado === 'past_due') {
        const hasta = d.finGracia === null ? null : diaYMes(d.finGracia);
        lista.push(franja('impago', {
            titulo: 'No hemos podido cobrar la cuota',
            tituloCorto: 'Cuota sin cobrar',
            texto: hasta === null ? '' : prop ? `Tienes hasta el ${hasta} para actualizar la tarjeta.` : `El propietario tiene hasta el ${hasta} para actualizar la tarjeta.`,
            accion: prop ? 'Actualizar la tarjeta' : null,
            destino: prop ? 'plan' : null,
        }));
    }

    // Vista del negocio (negra): lo ve el propietario y quien tiene `actividad.ver` (G137); el motivo, en Actividad.
    if (acceso !== null && acceso.viewer === 'business') {
        lista.push(franja('soporte', {
            titulo: 'Soporte de Reservaria está consultando tu negocio',
            tituloCorto: 'Soporte está en tu negocio',
            texto: `Desde las ${horaDe(acceso.opened_at)}.`,
            accion: 'Ver en Actividad',
            destino: 'actividad',
        }));
    }

    // Sin la fecha de fin no hay franja de prueba: el diseño no tiene texto sin los días (P0-9).
    if (d.estado === 'trialing' && d.finPrueba !== null) {
        const n = String(diasHasta(d.finPrueba, d.ahora));
        lista.push(franja('prueba', {
            titulo: `Te quedan ${n} días de prueba`,
            tituloCorto: `Quedan ${n} días de prueba`,
            texto: 'Elige tu plan para seguir recibiendo reservas.',
            accion: prop ? 'Contratar' : null,
            destino: prop ? 'plan' : null,
        }, `trial_ending:${diaIso(d.finPrueba)}`));
    }

    if (d.estado === 'cancel_scheduled' && d.fechaBaja !== null) {
        const dia = diaYMes(d.fechaBaja);
        lista.push(franja('baja', {
            titulo: `Tu negocio se da de baja el ${dia}`,
            tituloCorto: `Baja el ${dia}`,
            texto: 'Hasta entonces todo sigue igual. Después el panel queda en solo lectura.',
            accion: prop ? 'Anular la baja' : null,
            destino: prop ? 'plan' : null,
        }, `cancel_scheduled:${diaIso(d.fechaBaja)}`));
    }

    // Cupo del asistente al 100 % (G777, G24): corte del servicio, en rojo y sin cerrar, como el impago.
    const agotado = d.puedeVerPlan ? cupoAl(d.contadores, 1) : undefined;
    if (agotado !== undefined) {
        lista.push(franja('cupo100', {
            titulo: 'Se ha acabado el cupo del asistente',
            tituloCorto: 'Cupo del asistente al 100 %',
            texto: `Las conversaciones ya empezadas terminan; no empieza ninguna nueva hasta el ${diaSiguiente(agotado.period_end)}.`,
            accion: prop ? 'Comprar más' : 'Ver consumo',
            destino: 'consumo',
        }));
    }

    const cupo = d.puedeVerPlan ? cupoAl(d.contadores, 0.9, 1) : undefined;
    if (cupo !== undefined) {
        lista.push(franja('cupo', {
            titulo: 'Has usado el 90 % de tus conversaciones',
            tituloCorto: 'Cupo al 90 %',
            texto: 'Al llegar al 100 % el asistente deja de responder.',
            accion: prop ? 'Comprar más' : 'Ver consumo',
            destino: 'plan',
        }, `usage_90:${cupo.concept}_${diaIso(cupo.period_start)}`));
    }

    return ORDEN_FRANJAS.flatMap((tipo) => lista.filter((f) => f.tipo === tipo));
}

export interface FranjasVisibles {
    /** La más grave de las que no se han cerrado; null si no hay ninguna. */
    arriba: Franja | null;
    /** Las demás (también las cerradas): van a la campana. */
    enLaCampana: Franja[];
}

/**
 * Se ve una sola: la más grave de las que no se han cerrado (solo se cierran las amarillas). `cerradas`: las claves de
 * `dismissed_notices` (G129).
 */
export function franjasVisibles(franjas: readonly Franja[], cerradas: ReadonlySet<string>): FranjasVisibles {
    const arriba = franjas.find((f) => !(f.cerrable && f.clave !== null && cerradas.has(f.clave))) ?? null;
    return { arriba, enLaCampana: franjas.filter((f) => f !== arriba) };
}
